import {z} from "zod/v4-mini"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {GeometryAttributeNode, curveGeometryAttributeLikeMarker} from "#template/nodes/geometry-graph/attribute/attribute-node"
import {createCurveGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryValueData, GeometryValueRefData} from "#template/nodes/geometry-graph/interfaces"
import type {ScalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import {scalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {CurveGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {GeomBuilderContext, ValueRef} from "#template/geometry-processing/geometry-graph"
import {Primitives} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type CurveGeometryLine2Parameters = {
    x0: ScalarArg | null
    y0: ScalarArg | null
    x1: ScalarArg | null
    y1: ScalarArg | null
    numSegments: ScalarArg | null
}

const definition: TemplateNodeDefinition<CurveGeometryLine2Parameters> = {
    nodeClass: "CurveGeometryLine2",
    validation: {
        parameters: z.object({
            x0: z.nullable(scalarArg),
            y0: z.nullable(scalarArg),
            x1: z.nullable(scalarArg),
            y1: z.nullable(scalarArg),
            numSegments: z.nullable(scalarArg),
        }),
        returns: getTemplateNodeReturns(),
    },
}
const resolveScalarArg = (value: GeometryValueData | number): GeometryValueRefData | number => (typeof value === "number" ? value : value.ref)

@registerTemplateGraphNode
export class CurveGeometryLine2 extends GeometryAttributeNode<1, CurveGeometryLine2Parameters> implements CurveGeometryAttributeLikeNode {
    get [curveGeometryAttributeLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    protected override hasValidParameters(
        parameters: CurveGeometryLine2Parameters,
    ): parameters is CurveGeometryLine2Parameters & NonNullableParameters<CurveGeometryLine2Parameters> {
        return parameters.x0 !== null && parameters.y0 !== null && parameters.x1 !== null && parameters.y1 !== null && parameters.numSegments !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<CurveGeometryLine2Parameters>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<1> | null>; invalidator?: BuilderInlet<null>} {
        const {x0, y0, x1, y1, numSegments} = parameters

        const [x0Valid, x0Invalid] = scope.branch(evaluator.evaluate(x0))
        const [y0Valid, y0Invalid] = scope.branch(evaluator.evaluate(y0))
        const [x1Valid, x1Invalid] = scope.branch(evaluator.evaluate(x1))
        const [y1Valid, y1Invalid] = scope.branch(evaluator.evaluate(y1))
        const [numSegmentsValid, numSegmentsInvalid] = scope.branch(evaluator.evaluate(numSegments))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(x0Valid, y0Valid, x1Valid, y1Valid, numSegmentsValid, ctx),
            ([x0, y0, x1, y1, numSegments, ctx]) =>
                createCurveGeometryAttributeData(
                    applyGeometryOperator(
                        (x0: ValueRef | number, y0: ValueRef | number, x1: ValueRef | number, y1: ValueRef | number, numSegments: ValueRef | number) =>
                            Primitives.line2(ctx, x0, y0, x1, y1, numSegments),
                        resolveScalarArg(x0),
                        resolveScalarArg(y0),
                        resolveScalarArg(x1),
                        resolveScalarArg(y1),
                        resolveScalarArg(numSegments),
                    ),
                ),
            "curveGeometryLine2",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: scope.phi(x0Invalid, y0Invalid, x1Invalid, y1Invalid, numSegmentsInvalid)}
    }
}
